/**
 * Connector Management Service — wraps /api/admin/connectors/
 * Phase 4.7 — WQ-165
 */

export interface Connector {
  id: string;
  name: string;
  connector_type: string;
  description: string | null;
  origin: string | null;
  origin_country: string | null;
  origin_verified: boolean;
  transport: string | null;
  endpoint: string | null;
  health_url: string | null;
  lifecycle_state: string;
  mcp_config: Record<string, unknown> | null;
  sync_config: Record<string, unknown> | null;
  credentials_ref: string | null;
  last_health_check: string | null;
  last_health_status: string | null;
  consecutive_failures: number;
  auto_suspend_on_failure: boolean;
  max_consecutive_failures: number;
  kill_switch_enabled: boolean;
  enabled: boolean;
  total_invocations: number;
  last_invoked_at: string | null;
  registered_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConnectorAllowlist {
  id: string;
  tenant_id: string;
  connector_id: string;
  enabled: boolean;
  added_by: string | null;
  created_at: string;
}

export interface ConnectorStats {
  total: number;
  by_type: Record<string, number>;
  by_state: Record<string, number>;
  health_summary: Record<string, number>;
  enabled: number;
  disabled: number;
}

export interface HealthCheckResult {
  connector_id: string;
  name: string;
  status: string;
  consecutive_failures: number;
  auto_suspended: boolean;
  error: string | null;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// --- Registry ---

export async function listConnectors(
  authFetch: FetchFn,
  filters?: { connector_type?: string; lifecycle_state?: string; enabled_only?: boolean }
): Promise<Connector[]> {
  const params = new URLSearchParams();
  if (filters?.connector_type) params.set('connector_type', filters.connector_type);
  if (filters?.lifecycle_state) params.set('lifecycle_state', filters.lifecycle_state);
  if (filters?.enabled_only) params.set('enabled_only', 'true');
  const qs = params.toString();
  const res = await authFetch(`/api/admin/connectors/${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list connectors: ${res.status}`);
  return res.json();
}

export async function getConnector(authFetch: FetchFn, id: string): Promise<Connector> {
  const res = await authFetch(`/api/admin/connectors/${id}`);
  if (!res.ok) throw new Error(`Failed to get connector: ${res.status}`);
  return res.json();
}

export async function registerConnector(authFetch: FetchFn, data: {
  name: string; connector_type: string; description?: string;
  origin?: string; origin_country?: string; transport?: string;
  endpoint?: string; health_url?: string;
}): Promise<Connector> {
  const res = await authFetch('/api/admin/connectors/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to register connector: ${res.status}`);
  return res.json();
}

export async function updateConnector(
  authFetch: FetchFn, id: string, data: Partial<Connector>
): Promise<Connector> {
  const res = await authFetch(`/api/admin/connectors/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update connector: ${res.status}`);
  return res.json();
}

export async function transitionLifecycle(
  authFetch: FetchFn, id: string, newState: string
): Promise<Connector & { health_check_result?: Record<string, unknown> }> {
  const res = await authFetch(`/api/admin/connectors/${id}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_state: newState }),
  });
  if (!res.ok) throw new Error(`Failed to transition connector: ${res.status}`);
  return res.json();
}

export async function triggerHealthCheck(
  authFetch: FetchFn, id: string
): Promise<HealthCheckResult> {
  const res = await authFetch(`/api/admin/connectors/${id}/health-check`, { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to trigger health check: ${res.status}`);
  return res.json();
}

export async function getStats(authFetch: FetchFn): Promise<ConnectorStats> {
  const res = await authFetch('/api/admin/connectors/stats');
  if (!res.ok) throw new Error(`Failed to get connector stats: ${res.status}`);
  return res.json();
}

// --- Allowlist ---

export async function listAllowlist(
  authFetch: FetchFn, connectorId: string
): Promise<ConnectorAllowlist[]> {
  const res = await authFetch(`/api/admin/connectors/${connectorId}/allowlist`);
  if (!res.ok) throw new Error(`Failed to list allowlist: ${res.status}`);
  return res.json();
}

export async function addToAllowlist(
  authFetch: FetchFn, connectorId: string, tenantId: string
): Promise<ConnectorAllowlist> {
  const res = await authFetch(`/api/admin/connectors/${connectorId}/allowlist/${tenantId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to add to allowlist: ${res.status}`);
  return res.json();
}

export async function removeFromAllowlist(
  authFetch: FetchFn, connectorId: string, tenantId: string
): Promise<void> {
  const res = await authFetch(`/api/admin/connectors/${connectorId}/allowlist/${tenantId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to remove from allowlist: ${res.status}`);
}

export async function listTenantConnectors(
  authFetch: FetchFn, tenantId: string
): Promise<Connector[]> {
  const res = await authFetch(`/api/admin/connectors/by-tenant/${tenantId}`);
  if (!res.ok) throw new Error(`Failed to list tenant connectors: ${res.status}`);
  return res.json();
}
